import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';
import './App.css';
import Album from './Album';
import { NavLink } from 'react-router-dom';

//
// DynamicBanner renders the banner area.
// - On the Home route ("/"), it displays the banner image, title, and subtitle from data.banner.
// - On any other route, it removes the banner image, reduces the banner height,
//   and displays section-specific content.
function DynamicBanner({ data }) {
  const location = useLocation();

  if (location.pathname === "/") {
    // Home: show the default banner image with its title and subtitle.
    return (
      <div
        className="banner"
        style={{
          backgroundImage: `url(${data.banner.imageUrl})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        }}
      >
        <div className="banner-content">
          <div className="home">
            <h1>{data.banner.title}</h1>
            <p>{data.banner.subtitle}</p>
          </div>
        </div>
      </div>
    );
  } else {
    // For non-home sections:
    const sectionName = location.pathname.substring(1).toLowerCase();
    const section = data.sections.find(
      s => s.title.toLowerCase() === sectionName
    );
    let bannerTitle = section
      ? section.title
      : sectionName.charAt(0).toUpperCase() + sectionName.slice(1);

    // Override titles for specific sections.
    if (sectionName === "music") {
      bannerTitle = "Discography";
    } else if (sectionName === "about") {
      bannerTitle = "Biography";
    } else if (sectionName === "video") {
      bannerTitle = "Video";
    }

    return (
      <div
        className="banner section-banner"
        style={{ background: 'none', minHeight: '40vh' }}
      >
        <div className="banner-content">
          <h1>{bannerTitle}</h1>
          {section && section.content && sectionName === "about" ? (
            <div className="bio-info" style={{ margin: "0 auto" }}>
              {section.content.split('\n').map((paragraph, i) => (
                <p key={i} style={{ textAlign: 'justify' }}>{paragraph}</p>
              ))}
            </div>
          ) : (
            section &&
            section.content &&
            section.content.split('\n').map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))
          )}
        </div>
      </div>
    );
  }
}

//
// HomePage renders the DynamicBanner for the Home route.
function HomePage({ data }) {
  return (
    <>
      <DynamicBanner data={data} />
      {/* Additional Home content can be added here */}
    </>
  );
}

//
// SectionPage renders the DynamicBanner and additional content for sections.
// For Music, it renders album listings using the Album component.
function SectionPage({ data }) {
  const location = useLocation();
  const sectionName = location.pathname.substring(1).toLowerCase();
  const section = data.sections.find(
    s => s.title.toLowerCase() === sectionName
  );

  return (
    <>
      <DynamicBanner data={data} />
      {sectionName === "music" && section && section.albums && (
        <div className="music-section">
          {section.albums.map((album, index) => (
            <Album album={album} key={index} />
          ))}
        </div>
      )}
      {sectionName === "contact" && (
        <div className="music-section">
          <div className="album-container">
            <div className="album-content" style={{ justifyContent: 'center' }}>
              <div
                className="album-info"
                style={{ maxWidth: 'none', flex: 'none', textAlign: 'center' }}
              >
                <div className="contact-extra">
                  {/* Social Logos */}
                  <div className="social-logos">
                    <a
                      href="https://instagram.com/rozzotto_strings"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img className="logos" src="instagram_logo.png" alt="Instagram" />
                    </a>
                    <a
                      href="https://www.youtube.com/@agrid"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img className="logos" src="youtube_logo.png" alt="YouTube" />
                    </a>
                    <a
                      href="https://open.spotify.com/artist/6p0eQ0cYP0I7hDz9SsgOzr"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img className="logos" src="spotify_logo.png" alt="Spotify" />
                    </a>
                    <a
                      href="https://patreon.com/Rozzotto"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img className="logos" src="patreon_logo.png" alt="Patreon" />
                    </a>
                  </div>
                  {/* Donation / Support Form */}
                  <div className="donation-form">
                    <h3>Support</h3>
                    <form onSubmit={(e) => { e.preventDefault(); }}>
                      <input type="text" placeholder="Your Name" />
                      <br></br>
                      <input type="email" placeholder="Your Email" />
                      <br></br>
                      <textarea placeholder="Your Message"></textarea>
                      <br></br>
                      <button 
                        type="button"
                        onClick={() => window.location = 'mailto:info@rozzotto.com'}
                      >
                        Send
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

//
// VideoPage renders the DynamicBanner and displays YouTube videos.
// Instead of using videos from content.json, it fetches a text file with YouTube URLs,
// extracts the video IDs, and then renders embedded videos.
function VideoPage({ data }) {
  const [videoIds, setVideoIds] = useState([]);

  useEffect(() => {
    fetch('/videos.txt')
      .then(response => response.text())
      .then(text => {
        // Split file content by newlines and remove empty lines.
        const urls = text.split('\n').filter(url => url.trim() !== '');
        // Extract video IDs from the URLs.
        const ids = urls
          .map(url => extractVideoId(url))
          .filter(id => id !== null);
        setVideoIds(ids);
      })
      .catch(error => console.error('Error fetching video URLs:', error));
  }, []);

  // Function to extract the video ID from different YouTube URL formats.
  function extractVideoId(url) {
    try {
      const urlObj = new URL(url.trim());
      // For shortened URLs (e.g., "https://youtu.be/VIDEO_ID")
      if (urlObj.hostname === 'youtu.be') {
        return urlObj.pathname.slice(1); // Remove the leading '/'
      }
      // For standard URLs (e.g., "https://www.youtube.com/watch?v=VIDEO_ID")
      if (urlObj.hostname.includes('youtube.com')) {
        return urlObj.searchParams.get('v');
      }
    } catch (error) {
      console.error('Invalid URL:', url, error);
    }
    return null;
  }

  return (
    <>
      <DynamicBanner data={data} />
      <div className="album-container">
        <div className="album-content" style={{ justifyContent: 'center' }}>
          <div className="album-info" style={{ maxWidth: 'none', flex: 'none', textAlign: 'center' }}>
            {videoIds.map((videoId, index) => (
              <div key={index} className="video-wrapper" style={{ margin: '1rem 0' }}>
                <iframe
                  width="560"
                  height="315"
                  src={`https://www.youtube.com/embed/${videoId}?rel=0`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={`video-${index}`}
                ></iframe>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

//
// FloatingContact renders a small fixed section that stays visible on every page.
// It displays your contact email and the social logos.
function FloatingContact() {
  return (
    <div className="floating-contact">
      <div className="floating-contact-content">
        <p>info@rozzotto.com</p>
        <div className="social-logos">
          <a
            href="https://instagram.com/rozzotto_strings"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img className="logos" src="instagram_logo.png" alt="Instagram" />
          </a>
          <a
            href="https://www.youtube.com/@agrid"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img className="logos" src="youtube_logo.png" alt="YouTube" />
          </a>
          <a
            href="https://open.spotify.com/artist/6p0eQ0cYP0I7hDz9SsgOzr"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img className="logos" src="spotify_logo.png" alt="Spotify" />
          </a>
          <a
            href="https://patreon.com/Rozzotto"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img className="logos" src="patreon_logo.png" alt="Patreon" />
          </a>
        </div>
      </div>
    </div>
  );
}

//
// App Component sets up routing, loads the content from content.json, and includes the floating contact section.
function App() {
  const [data, setData] = useState({
    banner: { title: 'Loading...', subtitle: '', imageUrl: '', woodUrl: '' },
    sections: []
  });

  useEffect(() => {
    fetch('/content.json')
      .then(response => response.json())
      .then(jsonData => setData(jsonData))
      .catch(error => console.error('Error fetching content:', error));
  }, []);

  return (
    <Router>
      <div className="app-container">
        <nav className="menu">
          <ul>
            <li>
              <NavLink
                to="/"
                className={({ isActive }) => isActive ? "active" : ""}
              >
                Home
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/about"
                className={({ isActive }) => isActive ? "active" : ""}
              >
                About
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/music"
                className={({ isActive }) => isActive ? "active" : ""}
              >
                Music
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/video"
                className={({ isActive }) => isActive ? "active" : ""}
              >
                Video
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/contact"
                className={({ isActive }) => isActive ? "active" : ""}
              >
                Contact
              </NavLink>
            </li>
          </ul>
        </nav>

        <Routes>
          <Route path="/" element={<HomePage data={data} />} />
          <Route path="/about" element={<SectionPage data={data} />} />
          <Route path="/music" element={<SectionPage data={data} />} />
          <Route path="/video" element={<VideoPage data={data} />} />
          <Route path="/contact" element={<SectionPage data={data} />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        <FloatingContact />
      </div>
    </Router>
  );
}

export default App;
